import classNames from "classnames";
import { ref } from "Components/directives/ref";
import { useRef, useState } from "Shared/haunted/CustomHooks";
import i18next from "i18next";
import { html } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { LOGGED_IN_USER_IMAGE_SOURCES, ROLE_CODES } from "Services/constants";
import { TestIdDictionary } from "Services/test-ids/TestIdDictionary";
import { createAmericanAirlinesAccountSummaryUrl } from "Services/urls";
import { useNumberFormatter } from "Services/useNumberFormatter";
import { useEffectWithCleanup } from "Shared/haunted/CustomHooks";
import { useClickOutside } from "Shared/haunted/useClickOutside";
import { useReduxState } from "Shared/redux/useReduxState";

export interface Props {
	absoluteAction: string;
	bookingUrl: string;
	culture: string;
	handleLogout: () => void;
	onMouseEnter: () => void;
}

export const useLoggedInUserPill = (props: Props) => {
	const [userInfo] = useReduxState("userInfo");

	const { formatNumber } = useNumberFormatter();

	const dropdown = useRef<HTMLDivElement>(null);

	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

	const getImageSourceForLoggedInUser = () => {
		if (userInfo?.AmericanAirlines) return LOGGED_IN_USER_IMAGE_SOURCES.AMERICAN_AIRLINES;
		if (userInfo?.PeruCompra) return LOGGED_IN_USER_IMAGE_SOURCES.PERU_COMPRA;
		if (userInfo?.ChileCompra) return LOGGED_IN_USER_IMAGE_SOURCES.CHILE_COMPRA;
		if (userInfo?.Cug2) return LOGGED_IN_USER_IMAGE_SOURCES.CUG;

		return LOGGED_IN_USER_IMAGE_SOURCES.DEFAULT;
	};

	const profileUrl = () => `${props.bookingUrl}/V2/Profile?culture=${props.culture}&url=${props.absoluteAction}`;
	const reservationsUrl = () => `${props.bookingUrl}/V2Agency/Reservations`;

	useEffectWithCleanup(() => useClickOutside([dropdown], () => setIsDropdownOpen(false)), []);

	const aaInfoTemplate = () =>
		userInfo?.AmericanAirlines?.IsMember
			? html`
					<div class="flex w-full flex-row items-baseline justify-between text-n-blue">
						<span class="text-border-cug-gray-2 mr-1 whitespace-nowrap"
							>${i18next.t("Available-miles")}:</span
						><span class="text-[17px]/6 font-bold text-brand-tertiary"
							>${formatNumber({
								amount: userInfo?.AmericanAirlines?.Miles,
								isMiles: true,
								culture: props.culture,
								leadingSign: false,
							})}</span
						>
					</div>
					<div class="border-cug-gray-2 mb-4 flex w-full flex-row items-baseline justify-between text-n-blue">
						<span class="flex"
							>${unsafeHTML(
								i18next.t("AAdvantage {{-reg}}", {
									reg: '<span class="relative font-body -top-px">&reg;</span>',
								})
							)}:</span
						><span class="ml-2 text-[17px]/6 font-bold"
							>${userInfo?.AmericanAirlines?.AdvantageNumber}</span
						>
					</div>
				`
			: "";

	const handleNavigateToProfile = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (userInfo?.AmericanAirlines?.IsMember) {
			window.open(createAmericanAirlinesAccountSummaryUrl(props.culture), "_blank");
			return;
		}

		if (userInfo?.ChileCompra?.IsMember) {
			window.location.href = reservationsUrl();
			return;
		}

		window.location.href = profileUrl();
	};

	const profileButtonTemplate = () => html`
		<div
			class=${classNames(
				"with-arrow longer relative flex min-w-[162px] cursor-pointer appearance-none justify-center whitespace-normal rounded-full border-2 border-solid border-n-blue bg-n-blue px-[15px] py-[10px] pl-[15px] pr-[35px] text-center font-body text-[15px]/none font-bold normal-case leading-none tracking-normal text-white",
				"md:min-w-[180px] md:text-lg/none",
				"hover:border-n-blue hover:bg-white hover:text-n-blue"
			)}
			tabindex="-1"
			data-test-id=${TestIdDictionary.Menu.OpenAccountButton}
			@click=${handleNavigateToProfile}
		>
			<span>${i18next.t("My account")}</span>
			<i class="jsh-circle-chevron-right1 absolute right-1.25 text-[25px] font-normal vertical-center"></i>
		</div>
	`;

	const logoutButtonTemplate = () => html`
		<div
			class=${classNames(
				"relative inline-flex min-w-[162px] cursor-pointer appearance-none justify-center whitespace-normal rounded-full border-2 border-solid border-n-blue bg-white px-[15px] py-[10px] text-center font-body text-[15px]/none font-bold normal-case leading-none tracking-normal text-n-blue",
				"md:min-w-[180px] md:text-lg/none",
				"hover:border-n-blue hover:bg-n-blue hover:text-white"
			)}
			tabindex="-1"
			data-test-id=${TestIdDictionary.Menu.LogOutButton}
			@click=${props.handleLogout}
		>
			<span>${i18next.t("layout-logout")}</span>
		</div>
	`;

	const dropdownTemplate = () =>
		isDropdownOpen
			? html`
					<div
						class="absolute left-1/2 top-full z-50 mt-2 flex -translate-x-1/2 transform flex-col rounded-xl border border-solid border-be-gray-9 bg-white p-4"
					>
						<div class="flex flex-col items-center gap-2">
							${aaInfoTemplate()}${profileButtonTemplate()}${logoutButtonTemplate()}
						</div>
					</div>
				`
			: "";

	const htmlTemplate = () => {
		if (!userInfo?.Name) return "";

		const handleClick = (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			setIsDropdownOpen(!isDropdownOpen);
		};

		return html`
			<li class="flex h-full items-center justify-center" @mouseenter=${props.onMouseEnter}>
				<div
					ref=${ref(dropdown)}
					class=${classNames(
						"flex w-[150px] relative cursor-pointer items-center justify-between gap-2 rounded-full py-1 pl-1 pr-2 text-white",
						{
							"border-[3px] border-solid border-[#AF272F]": userInfo?.RoleCode === ROLE_CODES.STAFF,
							"bg-[#FFA400]": userInfo?.Dc,
							"bg-n-blue": !userInfo?.Dc,
						}
					)}
					@click=${handleClick}
				>
					<img class="h-7.5 w-7.5" src=${getImageSourceForLoggedInUser()} />
					<span class="w-23.25 truncate font-bold">
						${userInfo?.Name?.FirstName || ""} ${userInfo?.Name?.LastName || ""}
					</span>
					${dropdownTemplate()}
				</div>
			</li>
		`;
	};

	return { htmlTemplate };
};
